import { defineStore } from 'pinia';
import { api } from '@/services/http.service';
import { ElNotification, FormInstance } from 'element-plus';
import {
  GeoPaymentMethodItem,
  GlobalPaymentMethodItem,
  DEFAULT_GEO_PAYMENT_METHOD_FORM,
} from '@/models/ps-management/ps-management.model';
import {
  ConvertableCurrencyListModel,
  PaymentMethodCurrencyConverterItem,
  DEFAULT_CONVERTABLE_CURRENCY_LIST,
  DEFAULT_PAYMENT_METHOD_CURRENCY_CONVERTER_ITEM,
  EMPTY_PAYMENT_METHOD_CURRENCY_CONVERTER_ITEM,
} from '@/models/ps-management/currency-converter.model';
import { ref } from 'vue';
import { cloneDeep, isEqual } from 'lodash';
import { showErrorNotification } from '@/utils/show-notification.util';

export const currencyConverterFormRefs = ref<FormInstance[]>([]);

interface PaymentMethodCurrencyConverterListState {
  currentPaymentMethod: GeoPaymentMethodItem | GlobalPaymentMethodItem;
  convertableCurrencyList: ConvertableCurrencyListModel;
  paymentMethodCurrencyConverterList: PaymentMethodCurrencyConverterItem[];
  originalPaymentMethodCurrencyConverterList: PaymentMethodCurrencyConverterItem[];
  isPaymentMethodCurrencyConverterListLoading: boolean;
  isCurrencyConverterSettingsSidebarVisible: boolean;
}

export const usePaymentMethodCurrencyConverterListStore = defineStore({
  id: 'PaymentMethodCurrencyConverterListStore',
  state: (): PaymentMethodCurrencyConverterListState => ({
    currentPaymentMethod: structuredClone(DEFAULT_GEO_PAYMENT_METHOD_FORM),
    convertableCurrencyList: structuredClone(DEFAULT_CONVERTABLE_CURRENCY_LIST),
    paymentMethodCurrencyConverterList: [],
    originalPaymentMethodCurrencyConverterList: [],
    isPaymentMethodCurrencyConverterListLoading: false,
    isCurrencyConverterSettingsSidebarVisible: false,
  }),
  getters: {
    currentPaymentMethodId(state: PaymentMethodCurrencyConverterListState): string {
      return state.currentPaymentMethod.billingPaymentMethodId;
    },
    availableConvertToCurrencyListCounter(state: PaymentMethodCurrencyConverterListState): number {
      return state.convertableCurrencyList.currencyToList.length;
    },
    paymentMethodConverterListCount(state: PaymentMethodCurrencyConverterListState): number {
      return state.paymentMethodCurrencyConverterList.length;
    },
    isCurrencyConverterFormChanged(state: PaymentMethodCurrencyConverterListState): boolean {
      return !isEqual(
        state.paymentMethodCurrencyConverterList,
        state.originalPaymentMethodCurrencyConverterList,
      );
    },
  },
  actions: {
    setPaymentMethodCurrencyConverterListLoading(isLoading: boolean): void {
      this.isPaymentMethodCurrencyConverterListLoading = isLoading;
    },
    setCurrencyConverterSettingsSidebarVisible(isVisible: boolean): void {
      this.isCurrencyConverterSettingsSidebarVisible = isVisible;
    },
    setPaymentMethodCurrencyConverterList(currencyConverterList: PaymentMethodCurrencyConverterItem[]): void {
      this.paymentMethodCurrencyConverterList = currencyConverterList;
    },
    setCurrentPaymentMethod(paymentMethod: GeoPaymentMethodItem | GlobalPaymentMethodItem): void {
      this.currentPaymentMethod = paymentMethod;
    },
    addCurrencyConverter({ isDefault = false }: { isDefault?: boolean } = {}): void {
      this.paymentMethodCurrencyConverterList.push(
        isDefault
          ? { ...DEFAULT_PAYMENT_METHOD_CURRENCY_CONVERTER_ITEM }
          : { ...EMPTY_PAYMENT_METHOD_CURRENCY_CONVERTER_ITEM },
      );
    },
    removeCurrencyConverter(index: number): void {
      this.paymentMethodCurrencyConverterList.splice(index, 1);
    },
    getPaymentMethodCurrencyConverterList(): Promise<PaymentMethodCurrencyConverterItem[]> {
      this.setPaymentMethodCurrencyConverterListLoading(true);

      return api.psManagement.getPaymentMethodConverterListRequest
        .perform(this.currentPaymentMethodId)
        .then((currencyConverterList: PaymentMethodCurrencyConverterItem[]) => {
          this.paymentMethodCurrencyConverterList = currencyConverterList;

          return currencyConverterList;
        })
        .catch(error => {
          showErrorNotification(error);

          return [];
        })
        .finally(() => {
          this.setPaymentMethodCurrencyConverterListLoading(false);
        });
    },
    getConvertableCurrencyListRequest(): Promise<ConvertableCurrencyListModel> {
      if (!this.currentPaymentMethodId) {
        return Promise.resolve(this.convertableCurrencyList);
      }

      this.setPaymentMethodCurrencyConverterListLoading(true);

      return api.psManagement.getConvertableCurrencyListRequest
        .perform()
        .then((currencyList: ConvertableCurrencyListModel) => {
          this.convertableCurrencyList = currencyList;

          if (!this.paymentMethodConverterListCount) {
            this.addCurrencyConverter({ isDefault: true });
          }

          this.originalPaymentMethodCurrencyConverterList = cloneDeep(this.paymentMethodCurrencyConverterList);

          return currencyList;
        })
        .catch(error => {
          showErrorNotification(error);

          return this.convertableCurrencyList;
        })
        .finally(() => {
          this.setPaymentMethodCurrencyConverterListLoading(false);
        });
    },
    updatePaymentMethodCurrencyConverterList(): Promise<void> {
      this.setPaymentMethodCurrencyConverterListLoading(true);

      return api.psManagement.updatePaymentMethodCurrencyConverterRequest
        .perform({
          currencyConverterList: this.paymentMethodCurrencyConverterList,
          paymentMethodId: this.currentPaymentMethodId,
        })
        .then(() => {
          ElNotification({
            title: 'Success',
            message: 'Currency converter has been successfully updated',
            type: 'success',
          });
        })
        .catch(error => {
          showErrorNotification(error);
        })
        .finally(() => {
          this.setPaymentMethodCurrencyConverterListLoading(false);
        });
    },
    openCurrencyConverterSettings(paymentMethod: GeoPaymentMethodItem | GlobalPaymentMethodItem): void {
      this.setCurrencyConverterSettingsSidebarVisible(true);
      this.setCurrentPaymentMethod(paymentMethod);
    },
    closeCurrencyConverterSettings(): void {
      this.setCurrencyConverterSettingsSidebarVisible(false);
      this.setPaymentMethodCurrencyConverterList([]);
      this.setCurrentPaymentMethod(structuredClone(DEFAULT_GEO_PAYMENT_METHOD_FORM));
    },
  },
});
